import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiCall } from "../lib/apiClient";

/**
 * UC-003 distribuce — admin mutation + audit-log monitoring po triggeru.
 *
 * Audit-log endpoint vrací jen `{id, tenantId, action, createdAt}` (bez payloadu),
 * takže přesný filter per `distribution_run_id` není možný. Místo toho: hook vrací
 * všechny entries jejichž `action` patří do distribuční pipeline. Po POST trigger se
 * tabulka automaticky obnovuje každých 5 s po dobu 60 s (12 cyklů).
 */

export const DISTRIBUTION_ACTIONS = new Set<string>([
  "distribution_triggered",
  "distribution_skipped_no_consent",
  "distribution_completed",
  "magic_link_consumed",
  "payment_reminder_sent",
]);

export interface TriggerDistributionInput {
  budgetId: string;
  iban_cz: string;
  from_name_override?: string;
}

export interface TriggerDistributionResponse {
  distribution_run_id: string;
  budget_id: string;
  intents_created: number;
  emails_enqueued: number;
  qr_generated: number;
  skipped_count: number;
}

export interface AuditLogEntry {
  id: string;
  tenantId: string | null;
  action: string;
  createdAt: string;
}

export function useDistributionTrigger() {
  return useMutation<TriggerDistributionResponse, ApiError, TriggerDistributionInput>({
    mutationFn: ({ budgetId, iban_cz, from_name_override }) =>
      apiCall<TriggerDistributionResponse>(
        `/api/admin/budgets/${budgetId}/distribute`,
        {
          method: "POST",
          body: JSON.stringify({ iban_cz, from_name_override }),
        },
      ),
  });
}

export interface UseDistributionEventsOptions {
  /** Pokud true, hook se každých 5 s obnovuje (default false). Volající ho zapne na 60 s po POST. */
  polling: boolean;
}

export function useDistributionEvents(opts: UseDistributionEventsOptions) {
  return useQuery({
    queryKey: ["admin", "distribution", "audit-events"],
    queryFn: async () => {
      const all = await apiCall<AuditLogEntry[]>("/api/admin/audit-log");
      return all.filter((e) => DISTRIBUTION_ACTIONS.has(e.action)).slice(0, 20);
    },
    refetchInterval: opts.polling ? 5_000 : false,
  });
}
